import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import PageLayout from '@/components/feature/PageLayout';
import SeoHead from '@/components/feature/SeoHead';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import HeatBadge from '@/components/base/HeatBadge';
import RelationBoard from '@/components/base/RelationBoard';
import ChainTimeline from '@/components/base/ChainTimeline';
import ResponseCard from '@/components/base/ResponseCard';
import FaqAccordion from '@/components/base/FaqAccordion';
import CaseCard from '@/components/base/CaseCard';
import { cases } from '@/mocks/cases';
import { siteMeta } from '@/mocks/site';
import { getPerson } from '@/utils/content';
import type { CaseItem } from '@/types/content';

const caseList = cases as unknown as CaseItem[];

export default function CaseDetailPage() {
  const { id } = useParams<{ id: string }>();
  const item = caseList.find((c) => c.id === id);

  const busiest = item
    ? [...item.personIds].sort((a, b) => {
        const countA = item.relations.filter((r) => r.from === a || r.to === a).length;
        const countB = item.relations.filter((r) => r.from === b || r.to === b).length;
        return countB - countA;
      })[0]
    : '';

  const [centerId, setCenterId] = useState(busiest);

  useEffect(() => {
    setCenterId(busiest);
  }, [busiest]);

  if (!item) {
    return (
      <PageLayout>
        <section className="w-full px-4 md:px-8 pt-32 pb-24 text-center">
          <span className="w-16 h-16 mx-auto rounded-full bg-background-100 flex items-center justify-center text-3xl text-foreground-500">
            <i className="ri-search-eye-line" />
          </span>
          <h1 className="mt-5 font-heading font-black text-2xl text-foreground-950">没有找到这个事件</h1>
          <p className="mt-3 text-sm text-foreground-600">它可能已被合并或更新，去事件库看看其他的吧。</p>
          <Link
            to="/cases"
            className="mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-md bg-primary-500 text-background-50 font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            返回事件库
          </Link>
        </section>
      </PageLayout>
    );
  }

  const people = item.personIds.map((pid) => getPerson(pid)).filter(Boolean);
  const related = caseList.filter((c) => c.id !== item.id).slice(0, 3);

  const toc = [
    { href: '#relations', label: '人物关系图', icon: 'ri-git-branch-line' },
    { href: '#chain', label: '矛盾链条', icon: 'ri-git-commit-line' },
    { href: '#responses', label: '当事人回应', icon: 'ri-chat-quote-line' },
    { href: '#faq', label: '常见疑问', icon: 'ri-question-line' },
  ];

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: item.title,
      description: item.summary,
      datePublished: item.date,
      dateModified: item.date,
      articleSection: item.category,
      keywords: item.tags.join(','),
      author: { '@type': 'Organization', name: '911吃瓜' },
      publisher: { '@type': 'Organization', name: '911吃瓜' },
      mainEntityOfPage: `${siteMeta.domain}/case/${item.id}`,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: siteMeta.domain },
        { '@type': 'ListItem', position: 2, name: '吃瓜事件库', item: `${siteMeta.domain}/cases` },
        { '@type': 'ListItem', position: 3, name: item.title, item: `${siteMeta.domain}/case/${item.id}` },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: item.faqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    },
  ];

  return (
    <PageLayout>
      <SeoHead
        title={`${item.title} | 人物关系图与矛盾链条 · 911吃瓜`}
        description={`${item.summary.slice(0, 90)}——911吃瓜为你整理该事件的人物关系图、矛盾链条与当事人回应。`}
        keywords={`${item.title},${item.tags.join(',')},人物关系图,当事人回应`}
        path={`/case/${item.id}`}
        jsonLd={jsonLd}
      />

      <section className="w-full px-4 md:px-8 pt-24 md:pt-28">
        <Breadcrumbs items={[{ label: '吃瓜事件库', to: '/cases' }, { label: item.title }]} />

        <div className="mt-5 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          <div className="lg:col-span-7">
            <div className="flex flex-wrap items-center gap-3">
              <span className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold whitespace-nowrap">
                {item.category}
              </span>
              <HeatBadge heat={item.heat} size="md" />
            </div>
            <h1 className="mt-4 font-heading font-black text-2xl md:text-4xl text-foreground-950 leading-snug">
              {item.title}
            </h1>
            <p className="mt-3 text-sm md:text-base text-primary-600 font-medium">{item.subtitle}</p>
            <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-foreground-500">
              <span className="flex items-center gap-1 whitespace-nowrap">
                <i className="ri-calendar-line" />
                {item.date}
              </span>
              <span className="flex items-center gap-1 whitespace-nowrap">
                <i className="ri-eye-line" />
                {item.views} 阅读
              </span>
              <span className="flex items-center gap-1 whitespace-nowrap">
                <i className="ri-time-line" />
                约 {item.readTime}
              </span>
            </div>
          </div>

          <div className="lg:col-span-5">
            <img
              src={item.cover}
              alt={`${item.title} 事件主视觉`}
              title={`${item.title} - 911吃瓜`}
              className="w-full h-56 md:h-72 object-cover object-top rounded-lg"
            />
          </div>
        </div>

        <div className="mt-8 max-w-3xl">
          <p className="text-sm md:text-base leading-8 text-foreground-700">{item.summary}</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {item.tags.map((tag) => (
              <span key={tag} className="px-2.5 py-1 rounded-full bg-background-200 text-foreground-700 text-xs whitespace-nowrap">
                #{tag}
              </span>
            ))}
          </div>
        </div>
      </section>

      <div className="w-full px-4 md:px-8 mt-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          <aside className="lg:col-span-3 order-2 lg:order-1">
            <div className="lg:sticky lg:top-24">
              <div className="bg-background-100 border border-background-200 rounded-lg p-5">
                <p className="text-xs font-semibold tracking-[0.18em] text-primary-600 uppercase">本页目录</p>
                <nav className="mt-4 flex flex-col gap-1">
                  {toc.map((t) => (
                    <a
                      key={t.href}
                      href={t.href}
                      className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-foreground-700 hover:bg-background-200 hover:text-primary-700 transition-colors"
                    >
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className={t.icon} />
                      </span>
                      {t.label}
                    </a>
                  ))}
                </nav>
              </div>

              <div className="mt-5 bg-background-100 border border-background-200 rounded-lg p-5">
                <p className="text-sm font-semibold text-foreground-900 mb-3">关联人物</p>
                <ul className="space-y-3">
                  {people.map((person) => (
                    <li key={person!.id} className="flex items-center gap-3">
                      <img
                        src={person!.avatar}
                        alt={`${person!.name} 头像`}
                        title={`${person!.name} - ${item.title}`}
                        className="w-9 h-9 rounded-full object-cover object-top"
                      />
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-foreground-950">{person!.name}</span>
                        <span className="block text-xs text-foreground-500 truncate">{person!.role}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </aside>

          <div className="lg:col-span-9 order-1 lg:order-2 space-y-14">
            <section id="relations" className="scroll-mt-24">
              <h2 className="font-heading font-black text-xl md:text-2xl text-foreground-950 flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center text-lg">
                  <i className="ri-git-branch-line" />
                </span>
                人物关系图
              </h2>
              <p className="mt-3 text-sm leading-7 text-foreground-600">
                点击任意人物，即可把他切换为关系中心，查看他与事件中其他人的连线与关系标注。
              </p>
              <div className="mt-5">
                <RelationBoard
                  centerId={centerId}
                  relations={item.relations}
                  onSelect={(pid) => setCenterId(pid)}
                />
              </div>
            </section>

            <section id="chain" className="scroll-mt-24">
              <h2 className="font-heading font-black text-xl md:text-2xl text-foreground-950 flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-accent-100 text-accent-800 flex items-center justify-center text-lg">
                  <i className="ri-git-commit-line" />
                </span>
                矛盾链条
              </h2>
              <p className="mt-3 text-sm leading-7 text-foreground-600">
                把事件按时间顺序排成链条，看看矛盾是怎么一步步被推到台前的。
              </p>
              <div className="mt-5 bg-background-100 border border-background-200 rounded-lg p-5 md:p-7">
                <ChainTimeline steps={item.chain} />
              </div>
            </section>

            <section id="responses" className="scroll-mt-24">
              <h2 className="font-heading font-black text-xl md:text-2xl text-foreground-950 flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-secondary-100 text-secondary-900 flex items-center justify-center text-lg">
                  <i className="ri-chat-quote-line" />
                </span>
                当事人回应
              </h2>
              <p className="mt-3 text-sm leading-7 text-foreground-600">
                以下为该事件中各方公开发表的表态，已标注立场与来源，未回应的如实记录。
              </p>
              <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-5">
                {item.responses.map((res, index) => (
                  <ResponseCard key={`${res.personId}-${index}`} item={res} />
                ))}
              </div>
            </section>

            <section id="faq" className="scroll-mt-24">
              <h2 className="font-heading font-black text-xl md:text-2xl text-foreground-950 flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-background-200 text-foreground-800 flex items-center justify-center text-lg">
                  <i className="ri-question-line" />
                </span>
                常见疑问
              </h2>
              <div className="mt-5">
                <FaqAccordion items={item.faqs} />
              </div>
            </section>
          </div>
        </div>
      </div>

      <section className="w-full px-4 md:px-8 py-14 md:py-20">
        <div className="flex items-end justify-between gap-4 mb-6">
          <h2 className="font-heading font-black text-xl md:text-2xl text-foreground-950">相关推荐</h2>
          <Link to="/cases" className="text-sm font-semibold text-primary-600 hover:text-primary-700 whitespace-nowrap">
            更多事件 <i className="ri-arrow-right-line" />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {related.map((rc) => (
            <CaseCard key={rc.id} item={rc} />
          ))}
        </div>
      </section>
    </PageLayout>
  );
}